/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {GenericObject} from '@ion/core/src/types/entities';
import {FaultCodesModalProps} from '@ion/dls/src/components/assetdetails/current/FaultCodesModal';
import {DtcEventType} from '@ion/dls/src/types/types';
import {downloadFaultCodesCsv, getFaultCodeColumns, getFaultCodeRows} from './faultCodeRows';

export interface FaultCodesSummary {
  count: number;
  onViewDetails: () => void;
}

export type FaultCodesModalState = Omit<FaultCodesModalProps, 'workspaceId'>;

export interface UseFaultCodesModalOptions {
  asset: Device;
  faultData: GenericObject[];
  dtcEventType: DtcEventType;
  // builds the table only in the current theme ...
  isCurrent: boolean;
}

export interface UseFaultCodesModalResult {
  // the Diagnostics card's active fault count - omitted without any faults ...
  faultCodes?: FaultCodesSummary;
  modal: FaultCodesModalState;
}

/**
 * Drives the Diagnostics card's current theme Fault Codes modal - its open
 * state, the columns/rows built from the asset's fault events, the Export
 * handler and the card's active fault count. Columns and rows are built
 * only in the current theme (`isCurrent`); the legacy accordion shows its
 * own `FaultChipData` directly from `faultData` and never opens this modal.
 * A newly selected asset closes the modal, as it did before extraction.
 *
 * ```typescript
 *
 *   import {useFaultCodesModal} from '@/routes/tracking/useFaultCodesModal';
 *   ...
 *
 *   const {faultCodes, modal} = useFaultCodesModal({
 *     asset, faultData, dtcEventType, isCurrent
 *   });
 *
 * ```
 *
 * @param   options The selected `asset`, its raw `faultData`, their
 *                  `dtcEventType` and `isCurrent`, which builds the table
 *                  only when `true`.
 * @returns         The card's `faultCodes` summary and the modal's props.
 */
export const useFaultCodesModal = ({
  asset,
  faultData,
  dtcEventType,
  isCurrent
}: UseFaultCodesModalOptions): UseFaultCodesModalResult => {
  const [isOpen, setIsOpen] = useState(false);

  const onView = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    // a newly selected asset closes the previous asset's Fault Codes modal ...
    setIsOpen(false);
  }, [asset]);

  const columns = useMemo(() => (isCurrent ? getFaultCodeColumns() : []), [isCurrent]);
  const rows    = useMemo(() => (
    isCurrent ? getFaultCodeRows(faultData, dtcEventType, {
      groupName : asset?.accountName?.() ?? '',
      assetName : asset?.getAssetName?.() ?? '',
      assetType : asset?.getDevice?.()?.assetType ?? ''
    }) : []
  ), [isCurrent, faultData, dtcEventType, asset]);

  const onExport = useCallback(() => {
    downloadFaultCodesCsv(columns, rows, asset?.getAssetName?.() ?? '');
  }, [columns, rows, asset]);

  const faultCodes = useMemo(() => (
    rows.length > 0 ? {count: rows.length, onViewDetails: onView} : undefined
  ), [rows.length, onView]);

  const modal = useMemo(() => ({
    open       : isOpen && rows.length > 0,
    onClose,
    title      : translate('tracking:assetDetail.diagnostics.faultCodes.title'),
    viewTitle  : translate('tracking:assetDetail.diagnostics.faultCodes.viewTitle'),
    countLabel : translate(
      `tracking:assetDetail.diagnostics.faultCodes.${rows.length === 1 ? 'activeFault' : 'activeFaults'}`,
      {count: rows.length}
    ),
    exportLabel : translate('tracking:assetDetail.diagnostics.faultCodes.export'),
    closeLabel  : translate('tracking:assetDetail.diagnostics.faultCodes.close'),
    onExport,
    columns,
    rows
  }), [isOpen, rows, columns, onClose, onExport]);

  return {faultCodes, modal};
};

export default useFaultCodesModal;
